import { Box, Slab } from '../../render/parts'
import { DrawerFronts } from '../parts/storage'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Tall filing cabinet: recessed plinth, inset carcass, overhanging top cap and a
 * stack of drawer fronts. The cap is the only part at the declared footprint —
 * the carcass is inset a few millimetres so the two never share a plane.
 */

const WIDTH = 0.45
const DEPTH = 0.62
/** Full-height carcass (4 drawers). */
const HEIGHT = 1.05
/** Short "2 cajones" variant — the definition still declares HEIGHT. */
const SHORT_HEIGHT = 0.72

const PLINTH = 0.06
const CAP = 0.022
/** Carcass inset from the cap, in plan. */
const BODY_W = WIDTH - 0.012
const BODY_D = DEPTH - 0.008

/**
 * Card-holder plates on each drawer front. Mirrors the drawer maths of
 * `DrawerFronts` so the plates land on the same faces.
 */
function LabelHolders({ bottom, top, count }: { bottom: number; top: number; count: number }) {
  const gap = 0.008
  const leaf = (top - bottom - gap * (count + 1)) / count
  return (
    <group>
      {Array.from({ length: count }, (_, index) => {
        const y = bottom + gap + leaf / 2 + index * (leaf + gap)
        return (
          <Box
            key={index}
            tone="metal"
            size={[0.082, 0.024, 0.008]}
            position={[0, y - 0.028, BODY_D / 2 + 0.014]}
          />
        )
      })}
    </group>
  )
}

function FilingCabinet({ variant = 'x4' }: AssetContext) {
  const short = variant === 'x2'
  const height = short ? SHORT_HEIGHT : HEIGHT
  const drawers = short ? 2 : 4

  // The carcass dips 6mm below the plinth top and stops 6mm short of the cap
  // top, so no two parts ever end on the same plane.
  const bodyBottom = PLINTH - 0.006
  const bodyTop = height - CAP + 0.006
  const stackBottom = PLINTH + 0.008
  const stackTop = height - CAP - 0.006

  return (
    <group>
      {/* Recessed toe plinth. */}
      <Box
        tone="shellSoft"
        size={[WIDTH - 0.07, PLINTH, DEPTH - 0.06]}
        position={[0, PLINTH / 2, 0]}
      />

      {/* Carcass. */}
      <Box
        tone="shell"
        size={[BODY_W, bodyTop - bodyBottom, BODY_D]}
        position={[0, (bodyBottom + bodyTop) / 2, 0]}
      />

      {/* Top cap — the usable surface, slightly proud of the carcass. */}
      <Slab
        tone="shell"
        size={[WIDTH, DEPTH, CAP]}
        radius={0.008}
        position={[0, height - CAP / 2, 0]}
      />

      <DrawerFronts
        width={BODY_W}
        depth={BODY_D}
        bottom={stackBottom}
        top={stackTop}
        count={drawers}
      />
      <LabelHolders bottom={stackBottom} top={stackTop} count={drawers} />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'filing-cabinet',
  name: 'Archivador',
  category: 'storage',
  footprint: [WIDTH, DEPTH],
  /** Full-height unit. The "2 cajones" variant is shorter, at 0.72 m. */
  height: HEIGHT,
  surfaceHeight: HEIGHT,
  variants: [
    { id: 'x4', name: '4 cajones' },
    { id: 'x2', name: '2 cajones' },
  ],
  keywords: [
    'archivador',
    'archivero',
    'cajones',
    'almacenaje',
    'filing cabinet',
    'drawers',
    'storage',
  ],
  Component: FilingCabinet,
}
